import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, Spinner } from '@/components/icons';
import { PlansList } from '@/components/subscription/PlansList';
import { useModalContext } from '@/components/ui/modal-provider';
import { useBillingPortal } from '@/hooks/use-billing-portal';
import { useEntitlements } from '@/hooks/use-entitlements';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { clearSubscriptionCheckoutStarted, getSubscriptionCheckoutStarted } from '@/lib/analytics';
import WrapperLayout from '../../components/WrapperLayout';
import BillingPlansModal from './components/BillingPlansModal';
import EntitlementsOverview from './components/EntitlementsOverview';
import { InvoicesTable } from './components/InvoicesTable';

const CONFIRMED_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing']);
const SUBSCRIPTION_CONFIRMATION_RETRY_DELAY_MS = 2_000;
const SUBSCRIPTION_CONFIRMATION_MAX_ATTEMPTS = 5;

export default function Billing() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isConfirmingSubscription, setIsConfirmingSubscription] = useState(false);
    const [confirmationAttempts, setConfirmationAttempts] = useState(0);
    const { openModal } = useModalContext();
    const { isPortalLoading, openBillingPortal } = useBillingPortal();

    const {
        subscription,
        hasSubscription,
        isLoading: isSubscriptionLoading,
        refetch: refetchSubscription,
    } = useSubscriptionStatus();
    const {
        entitlements,
        isLoading: isEntitlementsLoading,
        isError: isEntitlementsError,
        refetch: refetchEntitlements,
    } = useEntitlements();

    useEffect(() => {
        if (searchParams.get('success')) {
            toast.success('Assinatura atualizada com sucesso!');
            setIsConfirmingSubscription(Boolean(getSubscriptionCheckoutStarted()));
            setConfirmationAttempts(0);
            void refetchSubscription();
            void refetchEntitlements();
            setSearchParams({});
        } else if (searchParams.get('canceled')) {
            toast.info('Processo de assinatura cancelado.');
            clearSubscriptionCheckoutStarted();
            setIsConfirmingSubscription(false);
            setConfirmationAttempts(0);
            setSearchParams({});
        }
    }, [refetchEntitlements, refetchSubscription, searchParams, setSearchParams]);

    useEffect(() => {
        const checkoutIntent = getSubscriptionCheckoutStarted();

        if (!checkoutIntent) {
            if (isConfirmingSubscription) {
                setIsConfirmingSubscription(false);
            }
            return;
        }

        if (checkoutIntent.hadSubscription) {
            clearSubscriptionCheckoutStarted();
            setIsConfirmingSubscription(false);
            return;
        }

        const hasConfirmedSubscription = Boolean(
            subscription?.id && CONFIRMED_SUBSCRIPTION_STATUSES.has(subscription.status),
        );
        const hasConfirmedPaidEntitlements = Boolean(
            entitlements?.active &&
            entitlements.plan !== 'TRIAL' &&
            CONFIRMED_SUBSCRIPTION_STATUSES.has(entitlements.status),
        );

        if (subscription?.id && hasConfirmedSubscription && hasConfirmedPaidEntitlements) {
            // The conversion is emitted by the backend now, from the Stripe
            // webhook reconcile. Firing it here as well would count the same
            // sale twice. The polling below stays: it is what turns this screen
            // from "Confirmando sua assinatura" into the real thing.
            clearSubscriptionCheckoutStarted();
            setIsConfirmingSubscription(false);
            return;
        }

        if (!isConfirmingSubscription) return;
        if (isSubscriptionLoading || isEntitlementsLoading) return;

        if (confirmationAttempts >= SUBSCRIPTION_CONFIRMATION_MAX_ATTEMPTS) {
            setIsConfirmingSubscription(false);
            return;
        }

        const retryTimer = window.setTimeout(() => {
            void refetchSubscription();
            void refetchEntitlements();
            setConfirmationAttempts((attempts) => attempts + 1);
        }, SUBSCRIPTION_CONFIRMATION_RETRY_DELAY_MS);

        return () => window.clearTimeout(retryTimer);
    }, [
        confirmationAttempts,
        entitlements?.active,
        entitlements?.name,
        entitlements?.plan,
        entitlements?.status,
        isConfirmingSubscription,
        isEntitlementsLoading,
        isSubscriptionLoading,
        refetchEntitlements,
        refetchSubscription,
        subscription?.id,
        subscription?.plan,
        subscription?.status,
    ]);

    const handleOpenPlansModal = () => {
        openModal({
            title: 'Alterar plano',
            content: (
                <BillingPlansModal
                    activePlanName={subscription?.plan}
                    nextPaymentDate={subscription?.current_period_end}
                />
            ),
            size: 'xl',
            bodyClassName: 'px-2 py-4 sm:px-4',
        });
    };

    return (
        <WrapperLayout
            title="Assinatura"
            subtitle="Gerencie sua assinatura e métodos de pagamento"
            leftIcon={Card}
        >
            <div className="space-y-12">
                {isSubscriptionLoading ? (
                    <div className="flex h-64 items-center justify-center">
                        <Spinner className="size-8 animate-spin text-muted-foreground" />
                    </div>
                ) : hasSubscription ? (
                    <>
                        <EntitlementsOverview
                            entitlements={entitlements}
                            isLoading={isEntitlementsLoading}
                            isError={isEntitlementsError}
                            onUpgradeClick={handleOpenPlansModal}
                            onManageSubscriptionClick={openBillingPortal}
                            isManageSubscriptionLoading={isPortalLoading}
                        />
                        <InvoicesTable />
                    </>
                ) : (
                    <>
                        <div className="space-y-6 duration-700 animate-in fade-in slide-in-from-bottom-4">
                            <div className="space-y-4">
                                <h2 className="text-3xl font-bold text-foreground">
                                    Invista na sua carreira
                                </h2>
                                <p className="max-w-2xl text-lg text-muted-foreground">
                                    Escolha o plano ideal e produza laudos psicológicos com mais
                                    rapidez, padronização e segurança técnica.
                                </p>
                            </div>
                            <div>
                                <PlansList fullWidth />
                            </div>
                        </div>
                        <EntitlementsOverview
                            entitlements={entitlements}
                            isLoading={isEntitlementsLoading}
                            isError={isEntitlementsError}
                            showPlanSummary={false}
                        />
                    </>
                )}
            </div>
        </WrapperLayout>
    );
}
